import { ADAPTIVE_MAX_BOOST, ADAPTIVE_MIN_BOOST, ADAPTIVE_SAMPLE_INTERVAL, ADAPTIVE_SMOOTHING, ADAPTIVE_TARGET_LUMA } from '../../brightness/constants';
/**
 * Side length (in pixels) of the tiny canvas the frame is downscaled to for luminance
 * analysis. 32x32 is plenty for a stable average and keeps the getImageData readback cheap.
 */
const SAMPLE_SIZE = 32;
/**
 * Fraction of the frame (centered) sampled for luminance. The subject is almost always
 * centered, so biasing towards the center tracks the person's exposure rather than a bright
 * window or lamp at the edges.
 */
const CENTER_CROP = 0.7;
/**
 * Computes an adaptive brightness multiplier from the live camera frame, emulating Google
 * Meet's "adjust brightness": it measures the average luminance of the (center-weighted)
 * subject and picks a boost that lifts a dark scene towards a target exposure, while leaving
 * already well-lit scenes untouched. The result is temporally smoothed to avoid flicker.
 */
export default class AdaptiveBrightness {
    _canvas;
    _ctx;
    _current = 1;
    _target = 1;
    _frame = 0;
    /**
     * Creates a new adaptive brightness analyzer with its own small sampling canvas.
     */
    constructor() {
        this._canvas = document.createElement('canvas');
        this._canvas.width = SAMPLE_SIZE;
        this._canvas.height = SAMPLE_SIZE;
        this._ctx = this._canvas.getContext('2d', { willReadFrequently: true });
    }
    /**
     * Updates and returns the current (smoothed) brightness multiplier for this frame.
     * Luminance is re-measured only every ADAPTIVE_SAMPLE_INTERVAL frames; the returned value
     * eases towards the latest measured target every call.
     *
     * @param {CanvasImageSource} source - The camera frame (video element or canvas).
     * @param {number} width - Source width in pixels.
     * @param {number} height - Source height in pixels.
     * @returns {number} The brightness multiplier to apply this frame.
     */
    update(source, width, height) {
        if (this._ctx && width > 0 && height > 0 && this._frame % ADAPTIVE_SAMPLE_INTERVAL === 0) {
            this._target = this._measure(source, width, height);
        }
        this._frame++;
        this._current += (this._target - this._current) * ADAPTIVE_SMOOTHING;
        return this._current;
    }
    /**
     * Measures the center-weighted mean luminance of the frame and maps it to a brightness
     * multiplier that would move that mean towards ADAPTIVE_TARGET_LUMA, clamped to the
     * configured boost range.
     *
     * @private
     * @param {CanvasImageSource} source - The camera frame.
     * @param {number} width - Source width in pixels.
     * @param {number} height - Source height in pixels.
     * @returns {number} The target brightness multiplier.
     */
    _measure(source, width, height) {
        const ctx = this._ctx;
        if (!ctx) {
            return this._target;
        }
        const cropW = width * CENTER_CROP;
        const cropH = height * CENTER_CROP;
        const sx = (width - cropW) / 2;
        const sy = (height - cropH) / 2;
        try {
            ctx.drawImage(source, sx, sy, cropW, cropH, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        }
        catch {
            // Source not ready yet — keep the previous target.
            return this._target;
        }
        const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
            sum += (0.2126 * data[i]) + (0.7152 * data[i + 1]) + (0.0722 * data[i + 2]);
        }
        const meanLuma = sum / (data.length / 4) / 255;
        const boost = ADAPTIVE_TARGET_LUMA / Math.max(meanLuma, 0.001);
        const clamped = Math.min(ADAPTIVE_MAX_BOOST, Math.max(ADAPTIVE_MIN_BOOST, boost));
        return clamped;
    }
    /**
     * Resets the smoothing state so the next enable ramps up from no boost again.
     *
     * @returns {void}
     */
    reset() {
        this._current = 1;
        this._target = 1;
        this._frame = 0;
    }
    /**
     * Releases the analyzer's canvas context.
     *
     * @returns {void}
     */
    dispose() {
        this._ctx = null;
    }
}
